import type { NavbarFolderItem, NavbarItem } from '@/shared/constant/navbar/NavbarItem';
import { NavbarItemTypeEnum } from '@/shared/constant/navbar/NavbarItem';
import { NavBarAlign } from '@tarslib/types/lib/publishedProject';
import clsx from 'clsx';
import React from 'react';
import { NavbarElement } from './components/navbar-element/NavbarElement';
import { NavbarMenuElement } from './components/navbar-menu-element/NavbarMenuElement';
import { NavbarSelectList } from './components/navbar-select-list/NavbarSelectList';
import { useNavbarGroupShowCount } from './hooks/useNavbarGroupShowCount';
import { NavbarGroupLevel, NavbarGroupType } from './type/NavbarGroupTypeLevel';

export type NavbarGroupItemLevelType =
  | {
      items: NavbarItem[];
      level: NavbarGroupLevel.Level1;
      type: NavbarGroupType.Default | NavbarGroupType.LineTab;
      align: NavBarAlign;
    }
  | {
      items: NavbarFolderItem[];
      level: NavbarGroupLevel.Level2;
      type: NavbarGroupType.Select | NavbarGroupType.LineTab;
    }
  | {
      items: NavbarFolderItem[];
      level: NavbarGroupLevel.Level3;
      type: NavbarGroupType.LineTab;
    };

export type NavbarGroupProps = NavbarGroupItemLevelType & {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
};

export const NavbarGroup = React.memo(function NavbarGroupComponent(props: NavbarGroupProps) {
  const { items, onClick, className, ...restProps } = props;
  const { getContainerRef, showList, moreMenuItem } = useNavbarGroupShowCount(items);

  const renderNavbarElementList = (list: NavbarItem[], isShowMore = true) => {
    return (
      <div
        ref={isShowMore ? undefined : getContainerRef}
        className={clsx(
          'flex flex-nowrap items-center gap-2',
          {
            ['max-os:hidden']: isShowMore,
            ['overflow-hidden os:h-0']: !isShowMore,
          },
          'max-os:flex-col max-os:items-start',
          restProps.level !== NavbarGroupLevel.Level1 ? 'max-os:gap-[6px]' : 'max-os:gap-[1px]',
          restProps.level === NavbarGroupLevel.Level1 && {
            ['os:justify-start']: restProps.align === NavBarAlign.Left,
            ['os:justify-center']: restProps.align === NavBarAlign.Center,
            ['os:justify-end']: restProps.align === NavBarAlign.Right,
          },
        )}
      >
        {list.map((x) => {
          if (x.type === NavbarItemTypeEnum.Menu) {
            return (
              <NavbarMenuElement
                key={x.id}
                value={x}
                groupLevelType={restProps}
                onClick={onClick}
              />
            );
          }

          return (
            <NavbarElement key={x.id} value={x} groupLevelType={restProps} onClick={onClick} />
          );
        })}
      </div>
    );
  };

  if (props.type === NavbarGroupType.Select) {
    return <NavbarSelectList items={props.items} className={className} onClick={onClick} />;
  }
  return (
    <div className={clsx(className)}>
      {renderNavbarElementList(items, false)}
      {renderNavbarElementList([...showList, ...(moreMenuItem ? [moreMenuItem] : [])], true)}
    </div>
  );
});
